import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import i18n from 'i18next';
import axios from 'axios';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';

const ResetPwd = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetCode = searchParams.get('resetCode') || '';
    const mode = searchParams.get('mode') || '';
    const [resetPassword, setResetPassword] = useState({});
    const [viewBag, setViewBag] = useState({});
    const [errors, setErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        // Call API to get initial data
        setLoadingPage(true);
        axios.get('/BBAuthentication/ResetPwd', { params: { resetCode: resetCode, mode: mode } })
            .then(response => {
                const data = response.data;
                if (data.RedirectPage) {
                    navigate(data.RedirectPage, { state: { ErrorMessage: data.ErrorMessage } })
                } else if (data.ErrorMessage) {
                    setErrorMessage(data.ErrorMessage)
                } else {
                    setResetPassword(data.resetPassword);
                    setViewBag(data.viewBag);
                }
            })
            .catch(error => {
                console.error('Error fetching data:', error);
            })
            .finally(() => {
                setLoadingPage(false);
            });
    }, []);

    const handleInputChange = (field, value) => {
        setResetPassword(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const matchPassword = () => {
        const { Password, ConfirmPassword } = resetPassword;

        if (!Password || !ConfirmPassword) return;

        const isMatch = Password === ConfirmPassword;

        if (isMatch) {
            setErrors(prev => ({ ...prev, ConfirmPassword: '' }));
        } else {
            setErrors(prev => ({ ...prev, ConfirmPassword: i18n.t('auth.reset_password.passwords_do_not_match') }));
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setErrorMessage('');

        if (!resetPassword.Password || !resetPassword.ConfirmPassword) {
            setErrors(prev => ({ ...prev, ConfirmPassword: i18n.t('auth.reset_password.passwords_do_not_match') }));
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post('/BBAuthentication/ResetPwd', resetPassword);
            const data = response.data;
            if (data.IsSuccess) {
                setIsSuccess(true);
            } else if (data.RedirectPage) {
                navigate(data.RedirectPage, data.ErrorMessage ? { state: { ErrorMessage: data.ErrorMessage } } : undefined);
            } else {
                setErrorMessage(data.ErrorMessage)
            }
        } catch (error) {
            setErrors(prev => ({ ...prev, ConfirmPassword: error.response?.data || i18n.t('auth.reset_password.an_error_occurred') }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {loadingPage && (
                <div className='text-center'>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            )}
            {!loadingPage && (
                <div className="login-inner">
                    <div className="body-inner">
                        {!isSuccess && (
                            <div id="UserIdForm" className="login-form">
                                <h4 className="card-title font-semibold">
                                    {viewBag.title}
                                </h4>
                                <p>{viewBag.title2}</p>

                                <div className="max-h-4 mb-4">
                                    {(errors.ConfirmPassword || errorMessage) && (
                                        <Message severity="error" className='w-full' text={errors.ConfirmPassword || errorMessage} />
                                    )}
                                </div>

                                <form onSubmit={handleSubmit} role="form" id="pwdForm">
                                    {viewBag.mode === 'passwordexpired' && (
                                        <div className="mb-4">
                                            <InputText type="password" value={resetPassword.OldPassword || ''} onChange={(e) => handleInputChange('OldPassword', e.target.value)}
                                                onKeyPress={handleKeyPress} placeholder={i18n.t('auth.reset_password.old_password')} className="w-full" required />
                                            {errors.OldPassword && (
                                                <Message severity="error" text={errors.OldPassword} />
                                            )}
                                        </div>
                                    )}

                                    <div className="mb-4">
                                        <InputText type="password" value={resetPassword.Password || ''} onChange={(e) => handleInputChange('Password', e.target.value)}
                                            onKeyPress={handleKeyPress} onBlur={matchPassword} placeholder={i18n.t('auth.reset_password.password')} className="w-full" required />
                                        {errors.Password && (
                                            <Message severity="error" text={errors.Password} />
                                        )}
                                    </div>

                                    <div className="mb-1">
                                        <InputText type="password" value={resetPassword.ConfirmPassword || ''} onChange={(e) => handleInputChange('ConfirmPassword', e.target.value)}
                                            onKeyPress={handleKeyPress} onBlur={matchPassword} placeholder={i18n.t('auth.reset_password.confirm')} className="w-full" required
                                        />
                                    </div>

                                    <div className="mt-4">
                                        <Button type="button" label={i18n.t('auth.next')} className="w-full" onClick={handleSubmit} loading={isLoading} disabled={isLoading} />
                                    </div>
                                </form>
                            </div>
                        )}
                        {isSuccess && (
                            <div className='login-form'>
                                <div className="text-2xl font-bold text-center">
                                    {i18n.t('auth.change_successful.change_password_successfully')}
                                </div>
                                <div className="text-center" style={{ marginTop: '10px' }}>
                                    <a href="/login" className='text-center text-primary' >
                                        {i18n.t('auth.change_successful.go_back_to_login')}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ResetPwd; 